import { useEffect } from "react";

/**
 * Hook utilitaire — met à jour title, meta description, OG, Twitter card et canonical.
 * Injecte aussi un JSON-LD schema.org si fourni. Restaure les anciennes valeurs au cleanup.
 */
export function useDocumentMeta({ title, description, image, type = "website", canonical, jsonLd } = {}) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prevTitle = document.title;
    const setMeta = (selector, attr, value) => {
      if (!value) return null;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [, name] = selector.match(/\[([\w:]+)="([\w:]+)"\]/) || [];
        if (name === "name") el.setAttribute("name", selector.match(/="([^"]+)"/)[1]);
        else if (name === "property") el.setAttribute("property", selector.match(/="([^"]+)"/)[1]);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return { el, attr, prev };
    };

    if (title) document.title = title;
    const restore = [];
    restore.push(setMeta('meta[name="description"]', "content", description));
    restore.push(setMeta('meta[property="og:title"]', "content", title));
    restore.push(setMeta('meta[property="og:description"]', "content", description));
    restore.push(setMeta('meta[property="og:type"]', "content", type));
    restore.push(setMeta('meta[property="og:image"]', "content", image));
    restore.push(setMeta('meta[name="twitter:card"]', "content", "summary_large_image"));
    restore.push(setMeta('meta[name="twitter:title"]', "content", title));
    restore.push(setMeta('meta[name="twitter:description"]', "content", description));
    restore.push(setMeta('meta[name="twitter:image"]', "content", image));

    let canonicalEl = null;
    if (canonical) {
      canonicalEl = document.head.querySelector('link[rel="canonical"]');
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", canonical);
    }

    let jsonLdEl = null;
    if (jsonLd) {
      jsonLdEl = document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.text = JSON.stringify(jsonLd);
      jsonLdEl.setAttribute("data-cockpit-jsonld", "true");
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      document.title = prevTitle;
      restore.filter(Boolean).forEach(({ el, attr, prev }) => {
        if (prev != null) el.setAttribute(attr, prev);
        else el.removeAttribute(attr);
      });
      if (jsonLdEl && jsonLdEl.parentNode) jsonLdEl.parentNode.removeChild(jsonLdEl);
    };
  }, [title, description, image, type, canonical, JSON.stringify(jsonLd || null)]);
}
